import isEqual from "lodash/isEqual";
import moment from "moment-timezone";
import PropTypes from "prop-types";
import React from "react";
import { Button } from "react-bootstrap";
import DeleteModal from "ui/dialog-modal/delete-modal";
import {
  FILTER_TYPE_MULTI_SELECT,
  FILTER_TYPE_SELECT,
  Filter,
} from "ui/filter";
import { Table, Column } from "ui/table";
import { sortByLabelAsc } from "util/sort-by-prop";
import {
  DEFAULT_TIME_FORMAT_WITH_TZ,
  formatDateWithAllTimezones,
  utcToLocalFormat,
} from "util/time";
import { Box } from "view/layout/box";
import { Page } from "view/layout/page";

import { formatTargets, formatStrategy } from "./utils";

function _getStartDateByStrategyType(row) {
  /**
   * We have 2 kinds of date format in Deploy Date column:
   * 1. A relative timestamp without timezone,
   * e.g. Dec 13th, 2019 05:29 am (All Timezones)
   * 2. A timestamp with local timezone for deployNow strategy,
   * e.g. Dec 13th, 2019 04:35 pm EST
   *
   * We need a custom sorting function so their raw data are converted into
   * timestamps with UTC (10:29 am UTC v.s. 09:35 pm UTC),
   * otherwise they won't be comparable.
   */
  const dateStr = row.deployed_immediately ? row.create_date : row.start_date;
  // Moment.js will cache the TZ result for subsequent calls.
  const localTZ = moment.tz.guess();

  return row.deployed_immediately
    ? moment.tz(dateStr, "UTC")
    : moment.tz(dateStr, localTZ).tz("UTC");
}

function _startDateSort(_u1, _u2, order = "asc", _field, rowA, rowB) {
  const sortDir = order === "asc" ? 1 : -1;
  const startDateA = _getStartDateByStrategyType(rowA);
  const startDateB = _getStartDateByStrategyType(rowB);

  if (startDateA.isSame(startDateB)) {
    return 0;
  }

  const compareResult = startDateA.isAfter(startDateB) ? 1 : -1;

  return compareResult * sortDir;
}

class ScheduledDeploymentListPage extends React.PureComponent {
  static get propTypes() {
    return {
      delete: PropTypes.func.isRequired,
      deployments: PropTypes.array,
    };
  }

  static get defaultProps() {
    return {
      deployments: [],
    };
  }

  constructor(props) {
    super(props);

    const { deployments } = props;

    this.state = {
      deleteModalId: null,
      tableData: deployments,
      targetOptions: this._getTargetOptions(deployments),
      strategyOptions: this._getStrategyOptions(deployments),
    };

    this.filterApi = null;

    this._deleteDeployment = this._deleteDeployment.bind(this);
    this._formatOperations = this._formatOperations.bind(this);
    this._formatStartDate = this._formatStartDate.bind(this);
    this._formatEndDate = this._formatEndDate.bind(this);
    this._onCloseDeleteModal = this._onCloseDeleteModal.bind(this);
    this._onFilterChange = this._onFilterChange.bind(this);
    this.getApi = this.getApi.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { deployments } = this.props;
    if (!isEqual(deployments, prevProps.deployments)) {
      this.setState({
        tableData: this.filterApi.getFilteredData(deployments),
        targetOptions: this._getTargetOptions(deployments),
        strategyOptions: this._getStrategyOptions(deployments),
      });
    }
  }

  static _getTargetValue(target) {
    return target.id;
  }

  _formatStartDate(startDate, row) {
    /**
     * We have two kinds of scheduled deployments:
     * 1. Deploy Now: All target devices get deployed right away after
     *    user's submission succeeds. Since we don't save original timezone for
     *    start_date in db, we utilize create_date which is saved in UTC.
     *
     *    E.g., a create_date '2019-11-11 20:40:33' can be converted into any
     *    local time based on browser's location, say, a user logs in at
     *    New York will see 'Nov 11th, 2019 03:40 pm EST'.
     *
     * 2. A specified start_date: It means all target devices should be deployed
     *    at this time of day, based on device's local clock. This is a relative
     *    timestamp and is the same for all affected devices.
     *
     *    E.g., we have a scheduled deployment with start_date
     *    '2019-11-08 22:00:00' and it will affect 2 devices, A and B.
     *    Device A in Chicago will be deployed at 'Nov 8th, 2019 10:00 pm CST',
     *    while device B in London will be deployed at
     *    'Nov 8th, 2019 10:00 pm GMT'.
     */
    const {
      create_date: createDate,
      deployed_immediately: wasDeployedImmediately,
    } = row;

    return wasDeployedImmediately
      ? utcToLocalFormat(createDate, DEFAULT_TIME_FORMAT_WITH_TZ)
      : formatDateWithAllTimezones(startDate);
  }

  _formatEndDate(endDate) {
    return formatDateWithAllTimezones(endDate);
  }

  _formatOperations(_val, deployment) {
    const _show = () => {
      this.setState({ deleteModalId: deployment.id });
    };

    return (
      <Button size="sm" onClick={_show} variant="secondary">
        <i className="fa fa-times" /> Delete
      </Button>
    );
  }

  _deleteDeployment() {
    return this.props.delete(this.state.deleteModalId);
  }

  _getFilterFields() {
    const { strategyOptions, targetOptions } = this.state;

    return [
      {
        label: "Strategy Name",
        name: "strategy",
        placeholder: "Strategy Name",
        showPlaceholder: true,
        type: FILTER_TYPE_SELECT,
        options: strategyOptions,
      },
      {
        getRecordValue: this.constructor._getTargetValue,
        label: "Targets",
        name: "targets",
        placeholder: "Targets",
        showPlaceholder: true,
        type: FILTER_TYPE_MULTI_SELECT,
        options: targetOptions,
      },
    ];
  }

  _getTargetOptions(deployments) {
    const targetOptions = {};

    deployments.forEach((deployment) => {
      deployment.targets.forEach((t) => {
        targetOptions[t.id] = {
          label: t.name,
          value: String(t.id),
        };
      });
    });

    return Object.values(targetOptions).sort(sortByLabelAsc);
  }

  _getStrategyOptions(deployments) {
    const strategyOptions = {};

    deployments.forEach((deployment) => {
      const { strategy } = deployment;
      strategyOptions[strategy] = {
        label: strategy,
        value: strategy,
      };
    });

    return Object.values(strategyOptions).sort(this._sortByLabelAsc);
  }

  _onCloseDeleteModal() {
    this.setState({ deleteModalId: null });
  }

  _onFilterChange() {
    const { deployments } = this.props;
    const tableData = this.filterApi.getFilteredData(deployments);

    this.setState({ tableData });
  }

  _renderFilterToolbar() {
    const filterFields = this._getFilterFields();

    return (
      <Filter
        onFilterChange={this._onFilterChange}
        getApi={this.getApi}
        fields={filterFields}
      />
    );
  }

  getApi(filterApi) {
    this.filterApi = filterApi;
  }

  render() {
    const { deleteModalId, tableData } = this.state;

    return (
      <Page title="Scheduled Deployments">
        <Box title="" noHeader={true}>
          <Table
            data={tableData}
            defaultSorted={[{ dataField: "start_date", order: "desc" }]}
            hover={true}
            pagination={true}
            search={true}
            toolbar={this._renderFilterToolbar()}
          >
            <Column
              key="id"
              dataField="id"
              dataSort={true}
              width="300px"
              isKey={true}
              hidden={true}
            >
              ID
            </Column>
            <Column
              key="strategy"
              dataField="strategy"
              dataSort={true}
              dataFormat={formatStrategy}
            >
              Strategy
            </Column>
            <Column
              key="targets"
              dataField="targets"
              dataSort={false}
              dataFormat={formatTargets}
            >
              Targets
            </Column>
            <Column
              key="start_date"
              dataField="start_date"
              dataSort={true}
              sortFunc={_startDateSort}
              dataFormat={this._formatStartDate}
            >
              Deploy Date
            </Column>
            <Column
              key="end_date"
              dataField="end_date"
              dataSort={true}
              dataFormat={this._formatEndDate}
            >
              End Date
            </Column>
            <Column
              key="operations"
              dataField=""
              dataSort={false}
              dataFormat={this._formatOperations}
              width="150px"
            >
              Operations
            </Column>
          </Table>
        </Box>
        <DeleteModal
          objectType="deployment"
          deleteId={deleteModalId}
          onDelete={this._deleteDeployment}
          onClose={this._onCloseDeleteModal}
        />
      </Page>
    );
  }
}

export {
  _getStartDateByStrategyType,
  _startDateSort,
  ScheduledDeploymentListPage,
};
